import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailSettingsContext } from '../../../modules/emailTemplates/EmailSettingsContext';
import { EmailSettings, EmailTemplate } from '../../../modules/emailTemplates/types';
import { SettingsSideBarCombinedProps } from './types';

/**
 * template contains the *saved* email settings, because it is unioned.
 */
const determineUnsaved = (currentSettings: EmailSettings, template: EmailTemplate) => {
  for (const key of Object.keys(currentSettings)) {
    if (currentSettings[key] !== template[key]) {
      return true;
    }
  }
  return false;
};

const usePresenter = (props: SettingsSideBarCombinedProps): SettingsSideBarCombinedProps => {
  const { emailTemplate } = props;
  const { t } = useTranslation();

  const {
    discardChanges,
    emailSettingsError,
    emailSettings,
    isUnsaved,
    saveButtonState,
    saveSettings,
    setEmailSettingsError,
    setEmailSettings,
    setEmailTemplate,
    setIsUnsaved,
    setIsValid,
    setSaveButtonState,
    isSettingsEmailInvalid,
    isSettingsFormValid,
  } = useContext(EmailSettingsContext);

  const emailTemplateInformation = useMemo(() => {
    return {
      emailSettings,
      emailTemplate,
      emailSettingsError,
    };
  }, [emailSettings, emailTemplate, emailSettingsError]);

  useEffect(() => {
    if (emailTemplate) {
      setEmailTemplate(emailTemplate);
    }
  }, [emailTemplate, setEmailTemplate]);

  useEffect(() => {
    const isValid = isSettingsFormValid(false);
    setIsValid(isValid);
  }, [emailSettings, isSettingsFormValid, setIsValid]);

  useEffect(() => {
    if (!emailTemplateInformation.emailTemplate) {
      return;
    }

    const isCurrentlyUnsaved = determineUnsaved(emailTemplateInformation.emailSettings, emailTemplateInformation.emailTemplate);
    setIsUnsaved(isCurrentlyUnsaved);
    if (isCurrentlyUnsaved && saveButtonState !== 'Loading') {
      setSaveButtonState('Default');
    }
    const invalidFormErrors = emailTemplateInformation.emailSettingsError;
    let isSenderEmailInvalid: boolean | undefined;
    let isReplyEmailInvalid: boolean | undefined;

    if (emailTemplateInformation.emailSettingsError.senderEmail) {
      isSenderEmailInvalid = isSettingsEmailInvalid(invalidFormErrors, 'senderEmail');
    }
    if (emailTemplateInformation.emailSettingsError.replyEmail) {
      isReplyEmailInvalid = isSettingsEmailInvalid(invalidFormErrors, 'replyEmail');
    }
    if ((isSenderEmailInvalid !== undefined && isSenderEmailInvalid)
      || (isReplyEmailInvalid !== undefined && isReplyEmailInvalid)) {
      setIsValid(false);
    }
    setEmailSettingsError(invalidFormErrors);
  }, [emailTemplateInformation, saveButtonState, isSettingsEmailInvalid, setEmailSettingsError, setIsUnsaved, setIsValid, setSaveButtonState]);


  const handleTextChange = (field: keyof EmailSettings) => {
    return (value: string) => {
      setEmailSettings({
        ...emailSettings,
        [field]: value,
      });
    };
  };

  const onDiscardButtonClicked = () => {
    discardChanges();
  };

  const onSubmitButtonClicked = async () => {
    const isValid = isSettingsFormValid(true);
    setIsValid(isValid);
    await saveSettings();
  };

  props = {
    ...props,
    text: {
      value: t('side_bar.settings.title'),
    },
    emailField: {
      label: {
        value: t('side_bar.settings.field_labels.email_subject'),
      },
      textInput: {
        textValue: emailSettings.emailSubject,
        onTextChanged: handleTextChange('emailSubject'),
      },
    },
    senderNameField: {
      label: {
        value: t('side_bar.settings.field_labels.sender_name'),
      },
      required: {
        value: '*',
      },
      textInput: {
        textValue: emailSettings.senderName,
        onTextChanged: handleTextChange('senderName'),
      },
      error: emailSettingsError.senderName === '' ? 'None' : 'Error',
      text: {
        value: emailSettingsError.senderName,
      },
    },
    senderEmailField: {
      label: {
        value: t('side_bar.settings.field_labels.sender_email'),
      },
      required: {
        value: '*',
      },
      textInput: {
        textValue: emailSettings.senderEmail,
        onTextChanged: handleTextChange('senderEmail'),
      },
      error: emailSettingsError.senderEmail === '' ? 'None' : 'Error',
      text: {
        value: emailSettingsError.senderEmail,
      },
    },
    replyField: {
      label: {
        value: t('side_bar.settings.field_labels.reply_email'),
      },
      textInput: {
        textValue: emailSettings.replyEmail,
        onTextChanged: handleTextChange('replyEmail'),
      },
      error: emailSettingsError.replyEmail === '' ? 'None' : 'Error',
      text: {
        value: emailSettingsError.replyEmail,
      },
    },
    saveButton: {
      text: {
        value: t('side_bar.settings.buttons.save'),
      },
      disabled: !isUnsaved,
      onClick: onSubmitButtonClicked,
      state: saveButtonState,
    },
    discardButton: {
      text: {
        value: t('side_bar.settings.buttons.discard'),
      },
      disabled: !isUnsaved,
      onClick: onDiscardButtonClicked,
    },
  };

  return props;
};

export default usePresenter;
